import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { KeycloakAuthService } from '../core/services/keycloak-auth.service';
import { ApiConfigService } from '../core/services/api-config.service';

export interface Secret {
  Id: string;
  IntegrationId: string;
  ExternalId: string;
  Name: string;
  Description: string;
  SecretRef: string;
}

export interface SecretDto {
  IntegrationId: string;
  ExternalId: string;
  Name: string;
  Description: string;
  Value: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class SecretService {
  private api: AxiosInstance | null = null;
  private platformId = inject(PLATFORM_ID);
  private authService = inject(KeycloakAuthService);
  private apiConfig = inject(ApiConfigService);

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      this.api = axios.create({
        headers: {
          'Content-Type': 'application/json'
        }
      });

      // Add request interceptor to dynamically set baseURL and include OAuth token
      this.api.interceptors.request.use((config) => {
        // Dynamically construct baseURL on each request to get current tenantId
        const baseUrl = this.apiConfig.getApiBaseUrl();
        const tenantId = this.apiConfig.getTenantId();
        config.baseURL = `${baseUrl}/${tenantId}`;

        const token = this.authService.accessToken;
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      });
    }
  }

  async getSecrets(): Promise<Array<Secret> | null> {
    if (!this.api) return null;

    try {
      const response = await this.api.get('/secret');
      return response.data;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }

  async getSecret(id: string): Promise<Secret | null> {
    if (!this.api) return null;

    try {
      const response = await this.api.get(`/secret/${id}`);
      return response.data;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }

  async updateSecret(id: string, secret: SecretDto): Promise<AxiosResponse | null> {
    if (!this.api) return null;

    try {
      const response = await this.api.put(`/secret/${id}`, secret);
      return response;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }

  async createSecret(secret: SecretDto): Promise<any | null> {
    if (!this.api) return null;

    try {
      // Normalize empty value to null
      if (secret.Value === null || secret.Value === undefined || secret.Value.trim() === '') {
        secret.Value = null;
      }

      const response = await this.api.post('/secret', secret);
      return response.data;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }

  async deleteSecret(id: string): Promise<AxiosResponse | null> {
    if (!this.api) return null;

    try {
      const response = await this.api.delete(`/secret/${id}`);
      return response;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }
}
